import React from "react";
import { cn } from "@/lib/utils";
import { isSafeHref } from "@/lib/url";

type AnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement>;

interface ExternalLinkProps extends Omit<
  AnchorProps,
  "target" | "rel" | "href"
> {
  href: string;
  children: React.ReactNode;
  "aria-label"?: string;
}

export const ExternalLink = React.forwardRef<
  HTMLAnchorElement,
  ExternalLinkProps
>(({ href, children, className, "aria-label": ariaLabel, ...props }, ref) => {
  const isSafe = isSafeHref(href);

  if (!isSafe) {
    const { target: _target, rel: _rel, ...rest } = props as AnchorProps;
    void _target;
    void _rel;
    return (
      <a
        ref={ref}
        role="link"
        aria-disabled={true}
        tabIndex={-1}
        className={cn(className)}
        {...rest}
        onClick={(e) => {
          e.preventDefault();
          props.onClick?.(e);
        }}
      >
        {children}
      </a>
    );
  }

  const label = ariaLabel ? `${ariaLabel} (opens in new tab)` : undefined;

  return (
    <a
      ref={ref}
      {...props}
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      aria-label={label}
      className={cn(className)}
    >
      {children}
      {!label && <span className="sr-only">&nbsp;(opens in new tab)</span>}
    </a>
  );
});

ExternalLink.displayName = "ExternalLink";
